import { Close } from "@material-ui/icons";
import {
  Article as ArticleIcon,
  Agriculture as AgricultureIcon,
  AreaChart as AreaChartIcon,
  Female as FemaleIcon,
  Grass as GrassIcon,
  Groups as GroupsIcon,
  Landscape as LandscapeIcon,
  Leaderboard as LeaderboardIcon,
  LocationOn as LocationOnIcon,
  Male as MaleIcon,
  Map as MapIcon,
  Nature as NatureIcon,
  People as PeopleIcon,
  Terrain as TerrainIcon,
  Yard as YardIcon,
} from "@mui/icons-material";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { API_URL } from "app/config";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import AnalyticsCard from "./AnalyticsCard";
import AnalyticsTimeSeries from "./AnalyticsTimeSeries";

const OnboardingAnalytics = ({
  analyticsData,
  getAllAnalyticsData,
  mainStateName,
}) => {
  const onboarding = analyticsData?.data?.onboarding;
  const onboardingSummary = onboarding?.summary;
  const topOnboardingStates = onboarding?.top_onboarding_states;
  const topOnboardingAgents = onboarding?.top_onboarding_agents;
  const topOrganizations = onboarding?.top_organizations;
  const topOnboardingDistricts = onboarding?.top_onboarding_districts || [];
  const [selectedOnboardingState, setSelectedOnboardingState] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStateDistricts, setSelectedStateDistricts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleStateClick = async (stateName) => {
    setSelectedOnboardingState(stateName);
    setOpenDialog(true);
    setSelectedStateDistricts([]);
    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/mis/api/v1/mis-analytics?use_cache=true`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ state_name: stateName }),
        }
      );

      const data = await response.json();
      const districts = data?.data?.onboarding?.top_onboarding_districts;

      setSelectedStateDistricts(districts);
    } catch (error) {
      console.error("Error fetching district data:", error);
      setSelectedStateDistricts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mainStateName) {
      setSelectedOnboardingState(mainStateName);
    } else {
      setSelectedOnboardingState(null);
    }
  }, [mainStateName]);

  useEffect(() => {
    if (mainStateName) {
      getAllAnalyticsData({
        state_name: mainStateName,
      });
    }
  }, [mainStateName, getAllAnalyticsData]);

  return (
    <Box>
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Total Farmers"
            count={onboardingSummary?.total_farmers}
            icon={PeopleIcon}
            color="#f97316"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Male Farmers"
            count={onboardingSummary?.total_farmers_male}
            icon={MaleIcon}
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Female Farmers"
            count={onboardingSummary?.total_farmers_female}
            icon={FemaleIcon}
            color="#6366f1"
            o
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Marginal Farmers"
            count={onboardingSummary?.marginal_farmers}
            icon={MapIcon}
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Small Farmers"
            count={onboardingSummary?.small_farmers}
            icon={PeopleIcon}
            color="#14b8a6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Medium Farmers"
            count={onboardingSummary?.medium_farmers}
            icon={PeopleIcon}
            color="#a855f7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Semi Medium Farmers"
            count={onboardingSummary?.semi_medium_farmers}
            icon={PeopleIcon}
            color="#eab308"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Large Farmers"
            count={onboardingSummary?.large_farmers}
            icon={PeopleIcon}
            color="#0ea5e9"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Total Farmer Geotagged"
            count={onboardingSummary?.total_farmers_geotagged}
            icon={MapIcon}
            color="#dc2626"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Total Farmer Plots"
            count={onboardingSummary?.total_farmer_plots}
            icon={LocationOnIcon}
            color="#2563eb"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Declared Land Holdings"
            count={`${onboardingSummary?.total_declared_land_holding_acres ||
              0} acres`}
            icon={LocationOnIcon}
            color="#9333ea"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Calculated Land Holdings"
            count={`${onboardingSummary?.total_calculated_land_holding_acres ||
              0} acres`}
            icon={MapIcon}
            color="#059669"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} marginBottom={4}>
        <Grid item xs={12} sm={6} md={4}>
          <AnalyticsCard
            title={
              mainStateName
                ? `Districts in ${mainStateName}`
                : "Top Onboarding States"
            }
            icon={LocationOnIcon}
            color="#14b8a6"
            sx={{ height: "100%", position: "relative" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {mainStateName
                ? topOnboardingDistricts?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom:
                          index !== topOnboardingDistricts.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                      >
                        {item.district_name || "Other"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#0ea5e9",
                          textAlign: "right",
                        }}
                      >
                        {item.total_count}
                      </Typography>
                    </Box>
                  ))
                : topOnboardingStates?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom:
                          index !== topOnboardingStates.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        cursor: !mainStateName ? "pointer" : "default",
                        "&:hover": !mainStateName
                          ? { background: "#f9fafb" }
                          : {},
                      }}
                      onClick={() => handleStateClick(item.state_name)}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#333",
                          width: "80%",
                        }}
                      >
                        {mainStateName
                          ? item.district_name
                          : item.state_name || "Other"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#14b8a6",
                          textAlign: "right",
                        }}
                      >
                        {item.total_count}
                      </Typography>
                    </Box>
                  ))}
            </Box>
          </AnalyticsCard>
        </Grid>

        {/* Top Onboarding Agents */}
        <Grid item xs={12} sm={6} md={4}>
          <AnalyticsCard
            title="Top Onboarding Agents"
            icon={LeaderboardIcon}
            color="#22c55e"
            sx={{ height: "100%" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {topOnboardingAgents?.map((agent, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom:
                      index !== topOnboardingAgents.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                  >
                    {agent.agent_name || "Other"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      // color: "#0ea5e9",
                      color: "#14b8a6",
                      textAlign: "right",
                    }}
                  >
                    {agent.total_count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AnalyticsCard>
        </Grid>

        {/*  Top Projects */}
        <Grid item xs={12} sm={6} md={4}>
          <AnalyticsCard
            title="Top Projects"
            icon={ArticleIcon}
            color="#6366f1"
            sx={{ height: "100%" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {topOrganizations?.map((organization, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom:
                      index !== topOrganizations.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                  >
                    {organization.organization_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#6366f1",
                      textAlign: "right",
                    }}
                  >
                    {organization.total_customers}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AnalyticsCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} marginBottom={4}>
        <Grid item xs={12}>
          <AnalyticsTimeSeries
            TimeSeries={onboarding?.timeseries}
            title="Onboarding Trend"
          />
        </Grid>
      </Grid>

      <Modal
        show={openDialog}
        onHide={() => setOpenDialog(false)}
        size="md"
        className="analytics-modal "
      >
        <AnalyticsCard
          title={` Districts in ${selectedOnboardingState}`}
          // icon={LocationOnIcon}
          color="#22c55e"
          sx={{
            marginBottom: 0,
            "&:hover": {
              transform: "translateY(0px)",
            },
          }}
        >
          <Close
            style={{
              position: "absolute",
              top: 10,
              right: 16,
              fontSize: 22,
              opacity: 0.8,
              color: "#22c55e",
            }}
            onClick={() => setOpenDialog(false)}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "100px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : selectedStateDistricts?.length > 0 ? (
              selectedStateDistricts.map((item, index) => (
                <>
                  <Box
                    key={index}
                    className="px-2"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom:
                        index !== selectedStateDistricts.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                    >
                      {item.district_name || "Other"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#0ea5e9",
                        textAlign: "right",
                      }}
                    >
                      {item.total_count}
                    </Typography>
                  </Box>
                </>
              ))
            ) : (
              <Typography textAlign="center" color="text.secondary">
                No districts found.
              </Typography>
            )}
          </Box>
        </AnalyticsCard>
      </Modal>
    </Box>
  );
};

export default OnboardingAnalytics;
